import { Button, Circle, Float, Icon, Menu, Portal } from '@chakra-ui/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { t } from 'i18next';
import { useMemo } from 'react';
import { FaSortAlphaDown, FaSortAlphaUpAlt } from 'react-icons/fa';
import { MdSort } from 'react-icons/md';

import { Tooltip } from '@/components/ui/tooltip';
import useMediaQuery from '@/hooks/use-media-query';

export const TableSortMenu = ({
  sort,
  columnsDef,
  navigate,
}: {
  sort: string;
  columnsDef: Array<Record<string, any>>;
  navigate: UseNavigateResult<''>;
}) => {
  const parsedSorting = useMemo(() => {
    if (!sort) return [];
    const isDesc = sort.startsWith('-');
    const id = isDesc ? sort.slice(1) : sort;
    return [{ id, desc: isDesc }];
  }, [sort]);

  const getOrder = (baseKey: string) => {
    const sortColumn = parsedSorting.find((s) => s.id === baseKey);
    if (sortColumn) {
      return sortColumn.desc ? 'desc' : 'asc';
    }
    return undefined;
  };
  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Menu.Root closeOnSelect={false}>
      <Menu.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.sort')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <MdSort />
            {isTooltipHidden && <span>{t('ui.sort')}</span>}
            {parsedSorting.length > 0 && (
              <Float offsetY="1">
                <Circle size="5" bg="primary" color="white" fontSize={'xs'}>
                  1
                </Circle>
              </Float>
            )}
          </Button>
        </Tooltip>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <Menu.ItemGroup title={t('ui.sort')}>
              {columnsDef.map(({ header, baseKey, options }) => (
                <Menu.Item
                  key={baseKey}
                  value={baseKey}
                  disabled={!options?.enableSorting === true}
                  onClick={() => {
                    if (!options?.enableSorting) return;
                    navigate({
                      search: (prev: Record<string, string>) => {
                        const currentOrder = getOrder(baseKey);

                        let newSortValue = '';
                        if (!currentOrder) {
                          // No sort → set ascending
                          newSortValue = baseKey;
                        } else if (currentOrder === 'asc') {
                          // Ascending → set descending
                          newSortValue = `-${baseKey}`;
                        } else if (currentOrder === 'desc') {
                          // Descending → clear sort
                          newSortValue = '';
                        }

                        return {
                          ...prev,
                          sort: newSortValue,
                        };
                      },
                    });
                  }}
                >
                  {typeof header === 'function' ? header() : header}
                  {getOrder(baseKey) === 'asc' && (
                    <Icon>
                      <FaSortAlphaDown />
                    </Icon>
                  )}
                  {getOrder(baseKey) === 'desc' && (
                    <Icon>
                      <FaSortAlphaUpAlt />
                    </Icon>
                  )}
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};
